<template>
  <div class="hire-action-column">
    <WdsMenu z-index="50" placement="bottom-end" manual-trigger @menu:open="load">
      <template #trigger="{ toggle }">
        <WdsButtonGhost
          :data-test="'contract-table-contract-row-actions-' + input.id"
          @click="toggle"
        >
          <WdsIcon name="overflow-menu-horizontal" :size="16" />
        </WdsButtonGhost>
      </template>

      <div v-if="column && !loading">
        <div v-if="column.canRemindWorkerForBilling && (isActive || isInactive)">
          <WdsMenuOption
            :data-test="'bulk-remind-to-submit-bill-button-' + column.id"
            @click="toggleRemindToBillModal"
          >
            {{ $t('Remind to submit bill') }}
          </WdsMenuOption>
          <WdsMenuDivider />
        </div>
        <WdsMenuOption
          :href="
            laravelNamedRoute(namedRoutes.hires.hire.view, { hire: column.id, tab: 'overview' })
          "
        >
          {{ $t('Manage hire') }}
        </WdsMenuOption>
        <WdsMenuOption :href="laravelNamedRoute(namedRoutes.jobs.show, { job: column.job.id })">
          {{ $t('Manage job') }}
        </WdsMenuOption>
        <div v-if="column.conversation?.id">
          <WdsMenuOption
            v-if="!isInactive"
            :href="
              laravelNamedRoute('messages.show', { id: column.conversation.id }) +
              '?managecontract=1'
            "
          >
            {{ $t('Manage contract') }}
          </WdsMenuOption>
          <WdsMenuOption :href="laravelNamedRoute('messages.show', { id: column.conversation.id })">
            {{ $t('Go to conversation') }}
          </WdsMenuOption>
        </div>

        <div v-if="column.conversation?.id">
          <WdsMenuOption
            v-if="column.canTerminateContract && isActive"
            :href="
              laravelNamedRoute('messages.show', { id: column.conversation.id }) +
              '?terminatecontract=1'
            "
          >
            {{ $t('Terminate contract') }}
          </WdsMenuOption>

          <WdsMenuOption
            v-if="column.canCancelContract && isOffered"
            :href="
              laravelNamedRoute('messages.show', { id: column.conversation.id }) +
              '?cancelcontract=1'
            "
          >
            {{ $t('Cancel hire') }}
          </WdsMenuOption>
        </div>

        <WdsMenuOption
          v-if="isInactive"
          :href="
            laravelNamedRoute('frontend.user.profile.hiredirect', { freelancer: column.worker.id })
          "
        >
          {{ $t('Re-hire') }}
        </WdsMenuOption>

        <WdsMenuOption
          v-if="!isDraft"
          :href="laravelNamedRoute('yjob.contract', { hire: column.id })"
        >
          {{ $t('Download contract PDF') }}
        </WdsMenuOption>
        <WdsMenuOption
          v-if="column.wcr?.id"
          :href="laravelNamedRoute('sds.pdf', { sds: column.wcr.id })"
        >
          {{ $t('Download Status Determination Statement') }}
        </WdsMenuOption>
        <WdsMenuOption
          v-if="column.conversation?.id && column.payReg?.paginatorInfo.total > 0 && isInactive"
          :href="
            laravelNamedRoute('messages.show', { id: column.conversation.id }) +
            '?show_feedback_modal=1'
          "
        >
          {{ $t('Review freelancer') }}
        </WdsMenuOption>
        <WdsMenuOption
          v-if="isInactive"
          :data-swaltext="
            $t('Do you want to terminate this job and terminate any ongoing job process?')
          "
          :data-swalconfirmtext="$t('Yes, mark the job as completed')"
          :data-swalcompletedtext="$t('The job closes now')"
          :data-formid="`form-job-close-${column.job.number}`"
          href="#"
          class="swal-delete"
          theme="DESTRUCTIVE"
        >
          {{ $t('End Job') }}
        </WdsMenuOption>
        <BaseHtmlForm
          :id="`form-job-close-${column.job.number}`"
          :action="`/job/${column.job.number}/close`"
          method="PATCH"
          hidden
        />
        <WdsMenuOption
          v-if="column.company.hasActiveWebhooks && !isDraft"
          :data-swaltext="$t('Are you sure you want to trigger Hire Accepted webhook?')"
          :data-swalconfirmtext="$t('Yes')"
          :data-swalcompletedtext="$t('Webhook triggered.')"
          :data-formid="`form-trigger-webhook-${column.number}`"
          href="#"
          class="swal-delete"
        >
          {{ $t('Trigger Webhook') }}
        </WdsMenuOption>
        <BaseHtmlForm
          :id="`form-trigger-webhook-${column.number}`"
          :action="`/integrations/webhook/hireAccepted/trigger/${column.id}`"
          method="POST"
          hidden
        />
      </div>
      <div
        v-else-if="loading"
        class="tw-px-4 tw-py-2"
        :data-test="'contract-table-contract-row-actions-loading-' + input.id"
      >
        <WdsSpinner indeterminate />
      </div>
      <div
        v-else
        class="tw-px-4 tw-py-2"
        :data-test="'contract-table-contract-row-actions-loading-' + input.id"
      >
        <WdsText :text="$t('No actions found available for this hire')" />
      </div>
    </WdsMenu>
  </div>
  <teleport to=".graphql-table-globals-wrapper">
    <ModalRemindToBill
      v-if="remindToBillModalIsOpen && column"
      :hire-ids="[column.number]"
      :job-name="column.job.name"
      :freelancer-name="column.worker.name"
      :is-open="remindToBillModalIsOpen"
      :default-message="column.company?.settings?.defaultMessageRemindWorkerToBill ?? undefined"
      class="text-left f-14"
      @reminder-sent="toggleRemindToBillModal"
      @closed="toggleRemindToBillModal"
    />
  </teleport>
</template>

<script setup lang="ts">
import {
  WdsButtonGhost,
  WdsIcon,
  WdsMenu,
  WdsMenuDivider,
  WdsMenuOption,
  WdsSpinner,
  WdsText,
} from '@worksome/wds-components'
import { computed, ref } from 'vue'
import { useLazyQuery } from '@vue/apollo-composable'
import { FragmentType, graphql, useFragment } from '@/gql'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { HireActiveStatus } from '@/gql/graphql'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'
import ModalRemindToBill from '@/components/ModalRemindToBill.vue'

const HireActionDataRequest = graphql(`
  fragment HireActionDataRequest on Hire {
    id
    number
    activeStatus
    canRemindWorkerForBilling
    company {
      id
      hasActiveWebhooks
      settings {
        defaultMessageRemindWorkerToBill
      }
    }

    canRemindWorkerForBilling
    canCancelContract
    canTerminateContract

    conversation {
      id
    }
    wcr {
      id
    }
    job {
      id
      name
      number
    }
    payReg: paymentRequests(first: 1, page: 1) {
      paginatorInfo {
        total
      }
    }
    worker {
      id
      name
    }
  }
`)

const HireActionColumn = graphql(`
  fragment HireActionColumn on Hire {
    id
  }
`)

const props = defineProps<{ row: FragmentType<typeof HireActionColumn> }>()

const input = computed(() => useFragment(HireActionColumn, props.row))

const { result, loading, load } = useLazyQuery(
  graphql(`
    query HirerActionColumnMenu($id: ID!) {
      hire(id: $id) {
        ...HireActionDataRequest
      }
    }
  `),
  () => ({ id: input.value.id }),
  {
    clientId: 'publicApiCustomFields',
  },
)

const column = computed(() => useFragment(HireActionDataRequest, result.value?.hire))

const remindToBillModalIsOpen = ref(false)

const toggleRemindToBillModal = () => {
  remindToBillModalIsOpen.value = !remindToBillModalIsOpen.value
}

const isDraft = computed(() => column.value?.activeStatus === HireActiveStatus.Draft)
const isOffered = computed(() => column.value?.activeStatus === HireActiveStatus.Offered)
const isActive = computed(() => column.value?.activeStatus === HireActiveStatus.Active)
const isInactive = computed(() =>
  column.value
    ? [HireActiveStatus.Ended, HireActiveStatus.Cancelled, HireActiveStatus.Terminated].includes(
        column.value.activeStatus,
      )
    : undefined,
)
</script>

<style scoped lang="scss">
.hire-action-column {
  :deep(.wds-menu) {
    width: max-content;
  }
}
</style>
